// Selectors
import * as AccountSelectors from '~/selectors/AccountSelectors';
import * as AnalyticsSelectors from '~/selectors/AnalyticsSelectors';
import * as CanvasSelectors from '~/selectors/CanvasSelectors';
import {getStyle} from '~/selectors/CatalogSelectors';
import * as DesignSelectors from '~/selectors/DesignSelectors';
import * as ProductSelectors from '~/selectors/ProductSelectors';
import * as QuoteSelectors from '~/selectors/QuoteSelectors';
import * as FundraisingSelectors from '~/selectors/FundraisingSelectors';
// Action Types
import AnalyticsTypes from '~/constants/types/AnalyticsTypes';
import CanvasTypes from '~/constants/types/CanvasTypes';
import DesignTypes from '~/constants/types/DesignTypes';
import QuoteTypes from '~/constants/types/QuoteTypes';
// Utils
import {getInferredStyleIdFromColorId} from '~/utils/url';
import {
  hashFunc,
  getCategoriesFromPath,
  getLabContactUsEvent,
  getLabShareEvent,
  getLabZoomEvent,
  getLabQuoteEvent,
  getWTOOptionsEvent,
  boolToNumber
} from './utils';
import {ID_VIEW_MAP} from '~/constants/ProductConstants';
import {withDesignTag} from '../../analytics';

export const Lab = {
  load: state => ({
    event: 'lab_load',
    product_style_id: ProductSelectors.getAnchorStyleId(state),
    product_style_name: ProductSelectors.getSelectedProductName(state),
    product_category: ProductSelectors.getSelectedProductCategoryName(state),
    product_color: ProductSelectors.getAnchorColor(state)?.name,
    product_type: ProductSelectors.getAnchorStyleType(state),
    lab_load_withdesign: withDesignTag(DesignSelectors.isDirty(state)),
    ad_promo_source: AnalyticsSelectors.getAdPromoSource(state),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    has_full_quote: boolToNumber(QuoteSelectors.hasFullQuote(state)),
    complementary_product_style_ids:
      FundraisingSelectors.getComplementaryProductIdsAsString(state) || 0
  }),
  saveStart: state => ({
    event: 'lab_save_start',
    product_style_id: ProductSelectors.getAnchorStyleId(state),
    product_color: ProductSelectors.getAnchorColor(state)?.name,
    has_full_quote: boolToNumber(QuoteSelectors.hasFullQuote(state)),
    complementary_product_style_ids:
      FundraisingSelectors.getComplementaryProductIdsAsString(state) || 0
  }),
  saveAddCartAttempt: state => {
    return [
      {
        event: 'lab_save_add_cart_attempt',
        product_style_id: ProductSelectors.getAnchorStyleId(state),
        product_color: ProductSelectors.getAnchorColor(state)?.name,
        has_full_quote: boolToNumber(QuoteSelectors.hasFullQuote(state)),
        complementary_product_style_ids:
          FundraisingSelectors.getComplementaryProductIdsAsString(state) || 0
      }
    ];
  },
  addToCart: (state, {payload}) => {
    const {recentlyAddedItems} = payload;
    let recentItemsTotal = 0;

    const eventItems = Object.values(recentlyAddedItems)?.map((item, index) => {
      const styleId =
        item?.styleId || getInferredStyleIdFromColorId(item?.colorId);
      const styleFromColor = getStyle(state, {styleId});
      const categoriesFromPath = getCategoriesFromPath(
        ProductSelectors.getStyleCategoryPath(state, styleId)
      );
      recentItemsTotal += item?.subTotal;

      return {
        item_id: styleId,
        item_name: styleFromColor?.name,
        affiliation: 'external_core',
        index,
        item_brand: styleFromColor?.manufacturer,
        item_category: categoriesFromPath?.rootCategory,
        item_category2: categoriesFromPath?.parentCategory,
        item_category3:
          categoriesFromPath?.primaryCategory ||
          styleFromColor?.primary_category_name,
        item_variant: item?.colorName,
        price: item?.unitPrice,
        quantity: item?.quantity
      };
    });
    const totalQty = eventItems.reduce((acc, item) => {
      const qty = +item.quantity;
      return isNaN(qty) ? acc : acc + qty;
    }, 0);
    return [
      {
        event: 'add_to_cart',
        ecommerce: {
          currency: 'USD',
          total_qty: totalQty,
          value: recentItemsTotal,
          items: eventItems
        },
        product_style_id: ProductSelectors.getAnchorStyleId(state),
        product_color: ProductSelectors.getAnchorColor(state)?.name,
        has_full_quote: boolToNumber(QuoteSelectors.hasFullQuote(state)),
        complementary_product_style_ids:
          FundraisingSelectors.getComplementaryProductIdsAsString(state) || 0
      }
    ];
  },
  saveComplete: (state, {payload}) => {
    if (payload.response?.success) {
      const {
        account_id,
        account_token,
        designid: design_id,
        first_time_saving: first_time_saver
      } = payload.response.design;
      return {
        event: 'lab_save_complete',
        account_id,
        account_token,
        design_id,
        email_256: hashFunc(payload.email),
        first_time_saver,
        internal_user: AccountSelectors.isInternalUser(state),
        product_category:
          ProductSelectors.getAnchorStyle(state)?.primary_category_name,
        product_color: ProductSelectors.getAnchorColor(state)?.name,
        product_type: ProductSelectors.getAnchorStyleType(state),
        product_style_id: ProductSelectors.getAnchorStyleId(state),
        product_style_name: ProductSelectors.getSelectedProductName(state),
        save_with_art: CanvasSelectors.hasArtIcon(state),
        total_qty: QuoteSelectors.getTotalQuantity(state),
        has_full_quote: boolToNumber(QuoteSelectors.hasFullQuote(state)),
        complementary_product_style_ids:
          FundraisingSelectors.getComplementaryProductIdsAsString(state) || 0
      };
    }
  },
  quoteStart: (_, {payload}) =>
    getLabQuoteEvent({
      eventName: 'lab_quote_start',
      quotePath: payload?.quotePath
    }),
  quoteForm: state =>
    getLabQuoteEvent({
      eventName: 'lab_quote_form',
      quotePath: 'quote',
      product_style_id: ProductSelectors.getAnchorStyleId(state),
      product_color: ProductSelectors.getAnchorColor(state)?.name,
      has_full_quote: boolToNumber(QuoteSelectors.hasFullQuote(state)),
      complementary_product_style_ids:
        FundraisingSelectors.getComplementaryProductIdsAsString(state) || 0
    }),
  buyForm: () =>
    getLabQuoteEvent({
      eventName: 'lab_quote_form',
      quotePath: 'buy'
    }),
  quote: (state, {payload: hasFullQuote}) => {
    if (hasFullQuote) {
      return {
        event: 'lab_quote_complete',
        color_count_back: QuoteSelectors.getColorCount(state)?.back || 0,
        color_count_front: QuoteSelectors.getColorCount(state)?.front || 0,
        email256: hashFunc(DesignSelectors.getCurrentDesignEmail(state)),
        internal_user: AccountSelectors.isInternalUser(state),
        oos_core_sizes: QuoteSelectors.getUnavailableCoreSizes(state),
        one_size_one_color: ProductSelectors.isSingleSizeAndStyle(state),
        postal_code: QuoteSelectors.getPostalCode(state),
        product_category:
          ProductSelectors.getAnchorStyle(state)?.primary_category_name,
        product_color: ProductSelectors.getAnchorColor(state)?.name,
        product_style_id: ProductSelectors.getAnchorStyleId(state),
        product_style_name: ProductSelectors.getSelectedProductName(state),
        total_qty: QuoteSelectors.getTotalQuantity(state),
        total_price: QuoteSelectors.getTotalPrice(state),
        zip_geodetect_change: AnalyticsSelectors.getPostalCodeChange(state),
        has_full_quote: boolToNumber(QuoteSelectors.hasFullQuote(state)),
        complementary_product_style_ids:
          FundraisingSelectors.getComplementaryProductIdsAsString(state) || 0
      };
    }
  },
  changeView: (_, {payload, meta}) => {
    const selectedView = ID_VIEW_MAP[payload];
    const formattedViewName = {
      leftSleeve: 'l sleeve',
      rightSleeve: 'r sleeve'
    };
    const validSources = {
      canvas_toolbar: true,
      mobile_canvas_toolbar: true
    };
    if (!validSources[meta?.source]) return null;
    return {
      event: 'lab_change_view',
      view_name: formattedViewName[selectedView] || selectedView
    };
  },
  sleeveStart: () => {
    return {
      event: 'lab_sleeve_start'
    };
  },
  sleeveAdded: (_, {payload, meta}) => {
    if (!payload || meta?.source !== 'new_sleeve_print_pricing') return null;
    return {
      event: 'lab_sleeve_added'
    };
  },
  zoomIn: () => getLabZoomEvent('in'),
  zoomOut: () => getLabZoomEvent('out'),
  shareEmail: () => getLabShareEvent('email'),
  shareFacebook: () => getLabShareEvent('facebook'),
  shareTwitter: () => getLabShareEvent('twitter'),
  sharePinterest: () => getLabShareEvent('pinterest'),
  sharePrint: () => getLabShareEvent('print'),
  shareLink: () => getLabShareEvent('copy'),
  quoteZip: (_, {payload}) => {
    const {source} = payload;
    if (!source) return;
    return {
      event: 'lab_quote_zip',
      step: source
    };
  },
  subtletyCheck: (state, {payload}) => {
    const {step, subtleColors, suggestion, suggestionId} = payload;
    let possibleSubtleInks = null;
    let subtleInks = null;

    if (suggestion?.suggestions) {
      possibleSubtleInks = suggestion.suggestions.find(
        suggestion => suggestion?.id === suggestionId
      );
    } else if (suggestion?.subtleInks) {
      possibleSubtleInks = suggestion;
    }

    if (possibleSubtleInks?.subtleInks) {
      const subtleInksSuggestions = Object.values(
        possibleSubtleInks.subtleInks
      );

      if (subtleInksSuggestions?.length > 0) {
        const subtleInksIds = subtleInksSuggestions[0];

        subtleInks = subtleInksIds?.reduce((acc, colorId) => {
          const color = subtleColors.find(color => colorId === color?.id);
          return acc ? `${acc}, ${color.name}` : `${color.name}`;
        }, '');
      }
    }

    return {
      event: 'lab_subletycheck',
      step,
      subtle_colors: subtleInks,
      product_style_id: ProductSelectors.getAnchorStyleId(state),
      product_color: ProductSelectors.getAnchorColor(state)?.name,
      has_full_quote: boolToNumber(QuoteSelectors.hasFullQuote(state)),
      complementary_product_style_ids:
        FundraisingSelectors.getComplementaryProductIdsAsString(state) || 0
    };
  },
  wtoQuoteOptionLoad: (_, {payload}) =>
    getWTOOptionsEvent({
      project: payload?.project,
      eventName: 'lab_quote_options'
    }),
  wtoQuoteOptionUpdate: (_, {payload}) =>
    getWTOOptionsEvent({
      project: payload?.project,
      eventName: 'lab_quote_options_change'
    }),
  fundraisingStart: () => {
    return {
      event: 'lab_fundraising_start'
    };
  },
  buyWithGroupGOFStart: () => {
    return {
      event: 'lab_gof_start'
    };
  },
  gofQuoteStart: () => {
    return {
      event: 'lab_quote_gof_start'
    };
  },
  gofQuoteComplete: () => {
    return {
      event: 'lab_quote_gof_complete'
    };
  },
  contactPhone: () => getLabContactUsEvent('phone'),
  contactEmail: () => getLabContactUsEvent('email')
};

const LabEvents = {
  [AnalyticsTypes.NDX_START]: Lab.load,
  [AnalyticsTypes.SAVE_FORM]: Lab.saveStart,
  [AnalyticsTypes.SAVE_ADD_CART_ATTEMPT]: Lab.saveAddCartAttempt,
  [AnalyticsTypes.SAVE_ADD_CART]: Lab.addToCart,
  [DesignTypes.SAVE_SUCCESS]: Lab.saveComplete,
  [AnalyticsTypes.QUOTE_FORM_START]: Lab.quoteStart,
  [AnalyticsTypes.QUOTE_FORM]: Lab.quoteForm,
  [AnalyticsTypes.BUY_FORM]: Lab.buyForm,
  [QuoteTypes.UPDATE_HAS_FULL_QUOTE]: Lab.quote,
  [CanvasTypes.CHANGE_VIEW]: Lab.changeView,
  [AnalyticsTypes.SLEEVE_PRINT_ACTIONS]: Lab.sleeveStart,
  [CanvasTypes.ACTIVATE_SLEEVE_PRINT]: Lab.sleeveAdded,
  [AnalyticsTypes.ZOOM_IN]: Lab.zoomIn,
  [AnalyticsTypes.ZOOM_OUT]: Lab.zoomOut,
  [AnalyticsTypes.SHARE_EMAIL]: Lab.shareEmail,
  [AnalyticsTypes.SHARE_FACEBOOK]: Lab.shareFacebook,
  [AnalyticsTypes.SHARE_TWITTER]: Lab.shareTwitter,
  [AnalyticsTypes.SHARE_PINTEREST]: Lab.sharePinterest,
  [AnalyticsTypes.PRINT_THIS]: Lab.sharePrint,
  [AnalyticsTypes.START_POSTAL_CODE_UPDATE]: Lab.quoteZip,
  [AnalyticsTypes.SUBTLETY_CHECK]: Lab.subtletyCheck,
  [AnalyticsTypes.WTO_OPTION_LOAD]: Lab.wtoQuoteOptionLoad,
  [AnalyticsTypes.WTO_OPTION_UPDATE]: Lab.wtoQuoteOptionUpdate,
  [AnalyticsTypes.NEXT_FUNDRAISING]: Lab.fundraisingStart,
  [AnalyticsTypes.BUY_WITH_GOF]: Lab.buyWithGroupGOFStart,
  [AnalyticsTypes.BUY_WITH_GOF_QUOTE_START]: Lab.gofQuoteStart,
  [AnalyticsTypes.BUY_WITH_GOF_QUOTE_COMPLETE]: Lab.gofQuoteComplete,
  [AnalyticsTypes.HELP_CALL]: Lab.contactPhone,
  [AnalyticsTypes.HELP_EMAIL]: Lab.contactEmail
};

export default LabEvents;
